import { ERROR, LOADING, READY, useVisualState } from '../../../hooks/useVisualState'
import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { get, post } from '../../../helpers/request'

import Button from '../../../components/Button/Button'
import Checkbox from '../../../components/Checkbox/Checkbox'
import ColorPicker from '../../../components/ColorPicker/ColorPicker'
import { Form } from '../../../components/Form/Form'
import GaleryUploader from '../../../components/GaleryUploader/GaleryUploader'
import HeroPreview from '../../../components/HeroPreview/HeroPreview'
import ImageUploader from '../../../components/ImageUploader/ImageUploader'
import Modal from '../../../components/Modal/Modal'
import ModalContent from '../../../components/Modal/ModalContent'
import ModalFooter from '../../../components/Modal/ModalFooter'
import ModalHeader from '../../../components/Modal/ModalHeader'
import ProgressBar from '../../../components/ProgressBar/ProgressBar'
import Select from '../../../components/Select/Select'
import SelectItem from '../../../components/Select/SelectItem'
import TextEditor from '../../../components/TextEditor/TextEditor'
import TextField from '../../../components/TextField/TextField'
import { closeEvent } from '../../../components/Modal/ModalEvents'
import t from '../../../helpers/i18n'
import { uploadFileHelper } from '../../../helpers/upload'
import useObjectState from '../../../hooks/useObjectState'
import { useParams } from 'react-router-dom'

const HEROS = {
  'default': 'Default',
  'flags': 'Flags',
  'soccer-ball': 'Soccer ball',
  'stadium': 'Stadium',
  'stars': 'Stars'
}

const emailValidation = (text) =>
  /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(text)
    ? false
    : t('validations.invalid_email')
const phoneValidation = (text) =>
  /^.{1,20}$/.test(text)
    ? false
    : t('validations.phone_number_is_invalid')

const SiteFormModal = ({ onCloseRequested }) => {
  const [formValid, setFormValid] = useState(false)
  const editSiteData = useRef()
  const pendingSiteData = useRef()
  const [[siteState, site], setSite] = useVisualState()
  const [useCustomHero, setUseCustomHero] = useState()
  const [uploading, setUploading] = useObjectState()
  const cancel = useRef(false)
  const uploadModal = useRef()
  const [hero, setHero] = useState()
  const [backgroundA, setBackgroundA] = useState()
  const [backgroundB, setBackgroundB] = useState()
  const [backgroundC, setBackgroundC] = useState()
  const [colorA, setColorA] = useState()
  const [colorB, setColorB] = useState()
  const [colorC, setColorC] = useState()
  const { siteId } = useParams()
  const contactGalery = useMemo(() => {
    return site && site.contact_galery
      ? site.contact_galery.map((id) => ({
          id,
          source: process.env.REACT_APP_IMAGE_BASE_URL + '/contact/' + id
        }))
      : null
  }, [site])

  const onValidityChange = useCallback(
    (valid) => {
      setFormValid(valid)
    },
    [setFormValid]
  )

  const handleSave = useCallback(
    (target) => {
      const paths = {
        shortcut_icon: 'icons',
        logo_horizontal: 'logos',
        logo_vertical: 'logos',
        contact_galery: 'contact'
      }
      uploadFileHelper(pendingSiteData.current, paths, setUploading)
        .then((data) =>
          Object.keys(data).reduce((final, key) => {
            if (data[key] instanceof Array) {
              final[key] = data[key].map((entry) => entry.id)
            } else {
              final[key] = data[key].id
            }
            return final
          }, {})
        )
        .then((data) => {
          if (!cancel.current) {
            post('/soccer/sites/' + siteId, {
              ...editSiteData.current,
              ...data
            })
              .then(() => {
                closeEvent(target, true)
              })
              .catch((error) => {
                // TODO: better error handling
                console.log(error)
              })
          }
        })
    },
    [siteId, setUploading]
  )

  const handleCancelUpload = useCallback(() => {
    cancel.current = true
    closeEvent(uploadModal.current, false)
  }, [])

  useEffect(() => {
    setSite(LOADING)
    get('/soccer/sites/' + siteId)
      .then((response) => {
        setUseCustomHero(!HEROS[response.hero_image])
        setSite(READY, response)
      })
      .catch((error) => {
        setSite(ERROR, error)
      })
  }, [siteId, setSite])

  return (
    <div>
      {siteState === READY && (
        <Modal onCloseRequested={onCloseRequested}>
          <ModalHeader title={t('modal_headers.Edit_Website')} />
          <ModalContent>
            <Form
              data={editSiteData}
              pending={pendingSiteData}
              onValidityChange={onValidityChange}
            >
              <div className='-band'>
                {t('titles.Homepage_settings')}
              </div>
              <TextField
                name='name'
                label={t('labels.Name')}
                initialValue={site.name}
                maxLength={100}
                minLength={1}
                required
              />
              <TextField
                name='page_title'
                label={t('labels.Page_Title')}
                initialValue={site.page_title}
                maxLength={100}
                minLength={1}
                required
              />
              <TextField
                name='page_description'
                label={t('labels.Page_Description')}
                initialValue={site.page_description}
                maxLength={100}
                minLength={1}
                required
              />
              <ImageUploader
                name='shortcut_icon'
                label={t('labels.Icon')}
                aspect={1}
                finalWidth={180}
                finalHeight={180}
                src={
                  site.shortcut_icon &&
                  process.env.REACT_APP_IMAGE_BASE_URL +
                    '/icons/' +
                    site.shortcut_icon
                }
              />
              <ImageUploader
                name='logo_horizontal'
                label={t('labels.Logo_Horizontal')}
                src={
                  site.logo_horizontal &&
                  process.env.REACT_APP_IMAGE_BASE_URL +
                    '/logos/' +
                    site.logo_horizontal
                }
              />
              <ImageUploader
                name='logo_vertical'
                label={t('labels.Logo_Vertical')}
                src={
                  site.logo_vertical &&
                  process.env.REACT_APP_IMAGE_BASE_URL +
                    '/logos/' +
                    site.logo_vertical
                }
              />
              <TextField
                name='domain'
                label={t('labels.Domain')}
                initialValue={site.domain}
                maxLength={100}
                minLength={1}
                required
              />
              <Select
                name='status'
                label={t('labels.Site_status')}
                initialValue={site.status}
              >
                <SelectItem value='setup'>{t('select_item.Setup')}</SelectItem>
                <SelectItem value='active'>{t('select_item.Active')}</SelectItem>
              </Select>
              <div className='-band'>{t('titles.Contact_Settings')}</div>
              <TextField
                name='contact_phone'
                label={t('labels.Contact_phone')}
                initialValue={site.contact_phone}
                onValidation={phoneValidation}
                maxLength={50}
              />
              <TextField
                name='contact_email'
                label={t('labels.Contact_E-Mail')}
                initialValue={site.contact_email}
                onValidation={emailValidation}
                minLength={5}
                maxLength={100}
              />
              <TextEditor
                name='services_description_raw'
                label={t('titles.Services_provided')}
                initialValue={site.services_description_raw}
                maxLength={5000}
              />
              <TextField
                name='address'
                label={t('labels.Office_address')}
                initialValue={site.address}
                maxLength={250}
              />
              <TextField
                name='google_maps_place_query'
                label={t('labels.Query_Google_Maps_for_map')}
                initialValue={site.google_maps_place_query}
                maxLength={1000}
              />
              <GaleryUploader
                name='contact_galery'
                label={t('titles.Contact_Photos')}
                sources={contactGalery}
              />
              <div className='-band'>{t('titles.Social_Networks')}</div>
              <TextField
                name='facebook_url'
                label={t('social_media.Facebook')}
                initialValue={site.facebook_url}
                maxLength={250}
              />
              <TextField
                name='instagram_url'
                label={t('social_media.Instagram')}
                initialValue={site.instagram_url}
                maxLength={250}
              />
              <TextField
                name='twitter_url'
                label={t('social_media.Twitter')}
                initialValue={site.twitter_url}
                maxLength={250}
              />
              <TextField
                name='youtube_url'
                label={t('social_media.YouTube')}
                initialValue={site.youtube_url}
                maxLength={250}
              />
              <div className='-band'>{t('titles.Site_Customization')}</div>
              <div className='-layout-11'>
                <Checkbox
                  name='contact_shortcut'
                  initialValue={site.contact_shortcut}
                  label={t('for_check.Show_contact_invitation')}
                />
                <Checkbox
                  name='search_shortcut'
                  initialValue={site.search_shortcut}
                  label={t('for_check.Show_display_search_teams_and_players')}
                />
                <Checkbox
                  name='instagram_galery'
                  initialValue={site.instagram_galery}
                  label={t('for_check.Show_gallery_Instagram')}
                />
                <Checkbox
                  name='stats_by_defeat'
                  initialValue={site.stats_by_defeat}
                  label={t('for_check.Show_stats_team_lost_MAN')}
                />
                <Checkbox
                  name='stats_by_received_goals'
                  initialValue={site.stats_by_received_goals}
                  label={t('for_check.Show_statistics_show_more_goals_against')}
                />
                <Checkbox
                  name='stats_general_own_goals'
                  initialValue={site.stats_general_own_goals}
                  label={t('for_check.Show_general_statistics_for_own_goals')}
                />
              </div>
              <div className='-layout-21'>
                {useCustomHero ? (
                  <TextField
                    name='hero_image'
                    label={t('labels.Hero image')}
                    initialValue={site.hero_image}
                    maxLength={100}
                    minLength={1}
                    required
                  />
                ) : (
                  <Select
                    name='hero_image'
                    label={t('labels.Hero image')}
                    initialValue={site.hero_image}
                    onChange={setHero}
                  >
                    {Object.keys(HEROS).map((hero) => (
                      <SelectItem key={hero} value={hero}>
                        {HEROS[hero]}
                      </SelectItem>
                    ))}
                  </Select>
                )}
                <Checkbox
                  initialValue={useCustomHero}
                  onChange={setUseCustomHero}
                  label={t('for_check.Use_custom_background')}
                />
              </div>
              {!useCustomHero && (
                <Fragment>
                  <div className='-layout-11'>
                    <ColorPicker
                      name='background_a'
                      label={t('labels.First_background_color')}
                      initialValue={site.background_a || '#0d47a1'}
                      onChange={setBackgroundA}
                    />
                    <ColorPicker
                      name='color_a'
                      label={t('labels.Color 1 del gráfico')}
                      initialValue={site.color_a || '#1976d2'}
                      onChange={setColorA}
                    />
                  </div>
                  <div className='-layout-11'>
                    <ColorPicker
                      name='background_b'
                      label={t('labels.Second_background_color')}
                      initialValue={site.background_b || '#1a237e'}
                      onChange={setBackgroundB}
                    />
                    <ColorPicker
                      name='color_b'
                      label={t('labels.Color 2 del gráfico')}
                      initialValue={site.color_b || '#1976d2'}
                      onChange={setColorB}
                    />
                  </div>
                  <div className='-layout-11'>
                    <ColorPicker
                      name='background_c'
                      label={t('labels.Third_background_color')}
                      initialValue={site.background_c || '#880e4f'}
                      onChange={setBackgroundC}
                    />
                    <ColorPicker
                      name='color_c'
                      label={t('labels.Color 3 del gráfico')}
                      initialValue={site.color_c || '#e91e63'}
                      onChange={setColorC}
                    />
                  </div>
                  <HeroPreview
                    heroName={hero}
                    backgroundA={backgroundA}
                    backgroundB={backgroundB}
                    backgroundC={backgroundC}
                    colorA={colorA}
                    colorB={colorB}
                    colorC={colorC}
                  />
                </Fragment>
              )}
              <ColorPicker
                name='primary_color'
                label={t('labels.Primary_color')}
                initialValue={site.primary_color}
              />
            </Form>
          </ModalContent>
          <ModalFooter>
            <div>
              <Button
                onClick={(event) => closeEvent(event.currentTarget, false)}
                label={t('buttons.Cancel')}
              />
            </div>
            <div>
              <Button
                disabled={!formValid}
                onClick={(event) => handleSave(event.currentTarget)}
                label={t('buttons.Save')}
              />
            </div>
          </ModalFooter>
          {uploading && (
            <Modal
              elementRef={uploadModal}
              onCloseRequested={handleCancelUpload}
            >
              <ModalHeader title={t('modal_headers.Uploading_images')} back={false} />
              <ModalContent dialog>
                {Object.keys(uploading).map((key) => (
                  <div key={key}>
                    <div className='text-label color-text'>
                      {key === 'contact_galery'
                        ? 'Fotos de contacto'
                        : pendingSiteData.current[key].value.name}
                    </div>
                    <ProgressBar percent={uploading[key]} />
                  </div>
                ))}
              </ModalContent>
            </Modal>
          )}
        </Modal>
      )}
    </div>
  )
}

export default SiteFormModal
